import React, {useRef, useState} from "react";
import classnames from "classnames";

import styles from "./FileDropzone.module.css";

interface FileDropzoneProps {
    onFileSelected: (file: File) => void
    /** Lower-case extension the file must have, for example ".zip". */
    accept: string
    maxSizeBytes: number
    title: string
    subtitle: string
    disabled?: boolean
}

/**
 * Drop area that also works as a button: a file can be dragged onto it, or it can be clicked (or
 * activated from the keyboard) to open the file picker.
 *
 * Validates the extension and the size before handing the file up, so an obvious mistake is caught
 * without a round trip to the server.
 */
export const FileDropzone: React.FC<FileDropzoneProps> = ({
                                                              onFileSelected,
                                                              accept,
                                                              maxSizeBytes,
                                                              title,
                                                              subtitle,
                                                              disabled = false
                                                          }) => {
    const [isDraggedOver, setIsDraggedOver] = useState(false);
    const [validationError, setValidationError] = useState<string | undefined>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const maxSizeMb = Math.round(maxSizeBytes / (1024 * 1024));

    const validate = (file: File): string | undefined => {
        if (!file.name.toLowerCase().endsWith(accept)) {
            return `Only ${accept.toUpperCase().replace(".", "")} files can be uploaded here.`;
        }
        if (file.size > maxSizeBytes) {
            return `The file is larger than the ${maxSizeMb} MB limit.`;
        }
        return undefined;
    };

    const handleFile = (file: File | undefined) => {
        if (!file) {
            return;
        }

        const error = validate(file);
        setValidationError(error);
        if (!error) {
            onFileSelected(file);
        }
    };

    const openFilePicker = () => {
        if (disabled || !fileInputRef.current) {
            return;
        }
        // Clearing first means selecting the same file twice in a row still fires a change event.
        fileInputRef.current.value = "";
        fileInputRef.current.click();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openFilePicker();
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        if (!disabled) {
            setIsDraggedOver(true);
        }
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDraggedOver(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDraggedOver(false);
        if (!disabled) {
            handleFile(event.dataTransfer.files?.[0]);
        }
    };

    return <div className={styles.wrapper}>
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            className={classnames(styles.dropzone, {
                [styles.dropzoneActive]: isDraggedOver,
                [styles.dropzoneDisabled]: disabled
            })}
            onClick={openFilePicker}
            onKeyDown={handleKeyDown}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <span className={styles.title}>{title}</span>
            <span className={styles.subtitle}>{subtitle}</span>
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                className={styles.hiddenInput}
                onChange={event => handleFile(event.target.files?.[0])}
            />
        </div>
        {validationError && <span className={styles.error} role="alert">{validationError}</span>}
    </div>
}
